import { Component, ComponentChild, ComponentChildren, createRef, render, RefObject, Ref } from 'preact'
import { forwardRef } from 'preact/compat'

interface Props {
  children: ComponentChildren
  mode?: ShadowRootMode
  shadowRef?: Ref<ShadowRoot>
}

class ContextProvider extends Component<{ context?: Record<string, unknown> }> {
  getChildContext() {
    return this.props.context
  }

  render() {
    return this.props.children
  }
}

const setRef = (ref: Ref<ShadowRoot>, value: ShadowRoot) => {
  if (typeof ref === 'function') {
    ref(value)
  } else if (ref != null) {
    ref.current = value
  }
}

class ShadowComponent extends Component<Props> {
  private shadowRoot: ShadowRoot
  private readonly placeholder: RefObject<HTMLDivElement>

  state = { initialized: false }

  constructor(props: Props) {
    super(props)
    this.placeholder = createRef<HTMLDivElement>()
  }

  shouldComponentUpdate(nextProps: Props) {
    this._update(nextProps)
    return true
  }

  componentDidMount() {
    const { mode = 'open', shadowRef } = this.props
    const parent = this.placeholder.current.parentElement
    this.shadowRoot = parent.shadowRoot || parent.attachShadow({ mode })
    this.setState({ initialized: true })
    this._update(this.props)
    setRef(shadowRef, this.shadowRoot)
  }

  componentWillUnmount() {
    this._update(this.props, true)
    setRef(this.props.shadowRef, null)
  }

  _update(props: Props, unrender = false) {
    render(
      unrender ? null : <ContextProvider context={this.context}>{props.children}</ContextProvider>,
      this.shadowRoot,
    )
  }

  render(): ComponentChild {
    if (!this.state.initialized) {
      return <div ref={this.placeholder} style={'display:none!important;'} />
    }
    return null
  }
}

export const Shadow = forwardRef<ShadowRoot, Omit<Props, 'shadowRef'>>(function Shadow(props, ref) {
  return <ShadowComponent {...props} shadowRef={ref} />
})

// TODO: createPortal causes problems with <style> tags rendering order, see
// https://github.com/preactjs/preact/issues/2849#issuecomment-2315214694
//
// export function Shadow({ children, mode = 'open' }: Props): JSX.Element {
//   const placeholderRef = useRef<HTMLDivElement>()
//   const [shadowRoot, setShadowRoot] = useState<ShadowRoot>()
//
//   useLayoutEffect(() => {
//     const root = placeholderRef.current!.parentElement!.attachShadow({ mode })
//     setShadowRoot(root)
//   }, [])
//
//   if (!shadowRoot) {
//     return <div ref={placeholderRef} style={'display:none!important;'} />
//   }
//
//   return createPortal(<>{children}</>, shadowRoot)
// }
